import React, { useState } from 'react';
import { permissionGroupsApi } from '../../api/permissionGroupsApi';

interface PermissionGroupImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
  operatorId?: string;
  scopeLabel?: string;
  t: (key: string) => string;
}

export const PermissionGroupImportModal: React.FC<PermissionGroupImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
  operatorId,
  scopeLabel,
  t,
}) => {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace' | 'merge'>('skip');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    if (loading) return;
    setImportFile(null);
    setImportMode('skip');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleImport = async () => {
    if (!importFile) {
      setError(t('pleaseSelectFile'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const result = await permissionGroupsApi.importPermissionGroups(
        importFile,
        importMode,
        operatorId,
      );

      let message =
        `${t('importPermissionGroupsSuccess')}\n` +
        `${t('importResultCreated')}: ${result.created}\n` +
        `${t('importResultUpdated')}: ${result.updated}\n` +
        `${t('importResultSkipped')}: ${result.skipped}`;

      if (result.warnings.length > 0) {
        message += `\n\n${t('importWarnings')}:\n- ${result.warnings.slice(0, 5).join('\n- ')}`;
        if (result.warnings.length > 5) {
          message += `\n- ... ${result.warnings.length - 5} ${t('importMoreWarnings')}`;
        }
      }

      setSuccess(message);
      onImported();

      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (err: any) {
      setError(err.response?.data?.message || t('failedToImportFile'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{t('importPermissionGroups')}</h2>
            {scopeLabel && <p className="text-sm text-gray-500 mt-1">{scopeLabel}</p>}
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-600 mb-5">{t('importPermissionGroupsHint')}</p>

          <div className="mb-5">
            <h3 className="text-lg font-medium text-gray-700 mb-3">{t('importMode')}</h3>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50">
                <input
                  type="radio"
                  name="permissionGroupImportMode"
                  checked={importMode === 'skip'}
                  onChange={() => setImportMode('skip')}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">{t('importPermissionGroupsModeSkip')}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50">
                <input
                  type="radio"
                  name="permissionGroupImportMode"
                  checked={importMode === 'replace'}
                  onChange={() => setImportMode('replace')}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">{t('importPermissionGroupsModeReplace')}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50">
                <input
                  type="radio"
                  name="permissionGroupImportMode"
                  checked={importMode === 'merge'}
                  onChange={() => setImportMode('merge')}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">{t('importPermissionGroupsModeMerge')}</span>
              </label>
            </div>
          </div>

          <div className="mb-5">
            <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step2UploadFile')}</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                id="permission-group-import-file"
              />
              <label
                htmlFor="permission-group-import-file"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="text-sm text-gray-700 font-medium">
                  {importFile ? importFile.name : t('clickToSelectFile')}
                </span>
                <span className="text-xs text-gray-500">
                  <span className="font-medium text-purple-600">JSON</span>
                  {' • '}
                  {t('importPermissionGroupsJsonHint')}
                </span>
              </label>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600 space-y-1">
            <p className="font-medium text-gray-700">{t('importPermissionGroupsFormatTitle')}</p>
            <p>• {t('importPermissionGroupsFormatName')}</p>
            <p>• {t('importPermissionGroupsFormatCode')}</p>
            <p>• {t('importPermissionGroupsFormatPermissions')}</p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4 text-sm whitespace-pre-line">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-4 text-sm whitespace-pre-line">
              {success}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('close')}
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={loading || !importFile}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? t('importLoading') : t('importPermissionGroups')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
